<!-- src/components/TinyEditor.vue -->
<template>
    <Editor v-model="model" :init="editorInit" :tinymceScriptSrc="tinymceScriptSrc" />
</template>

<script setup>
import { computed } from 'vue'
import Editor from '@tinymce/tinymce-vue'

import {G} from '@/_common';

const props = defineProps({ modelValue: { type: String, default: '' } })
const emit = defineEmits(['update:modelValue'])
const model = computed({
    get: () => props.modelValue,
    set: v => emit('update:modelValue', v)
})

// self-hosted path (copied from node_modules/tinymce to /public/tinymce)
const tinymceScriptSrc = '/tinymce/tinymce.min.js'

const editorInit = {
    /* --- Core self-hosted + license --- */
    license_key: 'gpl',
    base_url: '/tinymce',
    suffix: '.min',
    language: 'az',

    /* --- Misc --- */
    promotion: false,
    branding: false,        // hide “Powered by Tiny”
    elementpath: false,
    statusbar: true,

    /* --- UI --- */
    height: 520,
    menubar: 'file edit view insert format tools table',
    // toolbar:
    //     'undo redo | styles | bold italic underline strikethrough | ' +
    //     'alignleft aligncenter alignright alignjustify | ' +
    //     'bullist numlist outdent indent | forecolor backcolor | ' +
    //     'link image media table | code fullscreen preview',

    toolbar: [
        // editing
        'undo redo | fontsize | bold italic underline | forecolor backcolor |',
        // alignment & lists
        'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | h1 h2 h3 blockquote | removeformat |',
        // images/media/tables
        'link image media table |',
        // view
        'code fullscreen preview'
    ].join(' '),

    /* --- Plugins you actually use (ensure they exist under /public/tinymce/plugins) --- */
    plugins: 'lists link image media table code preview fullscreen searchreplace autosave visualblocks visualchars charmap pagebreak anchor wordcount quickbars',

    /* --- Content behavior --- */
    // content_style: 'body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,"Helvetica Neue",Arial,sans-serif; font-size:14px}',
    content_style: `
        body{font-family:system-ui,-apple-system,Segoe UI,Roboto,Ubuntu,Arial,sans-serif;font-size:14px}
        img{transition:transform .15s ease;max-width:100%;height:auto;margin:8px;}
        img.img-fluid{max-width:100%;height:auto}
        img.img-left { float:left; margin:0 1em 1em 0; }
        img.img-right { float:right; margin:0 0 1em 1em; }
        img.rounded{border-radius:12px}
        img.shadow{box-shadow:0 8px 24px rgba(0,0,0,.2)}
        figure.image figcaption{color:#666;font-size:12px;text-align:center}
    `,

    /* --- Table options (nice defaults) --- */
    table_default_attributes: { border: '0' },
    table_default_styles: { width: '100%', 'border-collapse': 'collapse' },
    table_advtab: true,


    // --- Image dialog options (for insert & edit) ---
    image_title: true,
    image_description: true,   // alt text field
    image_caption: true,       // wrap in <figure> with <figcaption>
    image_dimensions: true,

    // class dropdown inside the Image dialog
    image_class_list: [
        { title: 'None', value: '' },
        { title: 'Float left', value: 'img-left' },
        { title: 'Float right', value: 'img-right' },
        { title: 'Responsive', value: 'img-fluid' },
        { title: 'Rounded', value: 'rounded' },
        { title: 'Shadow', value: 'shadow' },
        { title: 'Rounded + Shadow', value: 'rounded shadow' }
    ],

    // --- Style presets in the "Styles" dropdown (applies when an image is selected) ---
    // style_formats: [
    //     {
    //         title: 'Image styles', items: [
    //             { title: 'Responsive', selector: 'img', classes: 'img-fluid' },
    //             { title: 'Rounded', selector: 'img', classes: 'rounded' },
    //             { title: 'Shadow', selector: 'img', classes: 'shadow' },
    //             { title: 'Rounded + Shadow', selector: 'img', classes: 'rounded shadow' }
    //         ]
    //     },
    //     // you can add text styles here too
    // ],

    // --- Quickbars: show tools when user selects an image or text ---
    quickbars_selection_toolbar: '', //'bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | quicklink | h2 h3 blockquote | removeformat',
    quickbars_insert_toolbar: '', // 'image media table',
    quickbars_image_toolbar: 'alignleft aligncenter alignright',
    // quickbars_image_toolbar: 'alignleft aligncenter alignright | rotate90L rotate90R',
    // `imageoptions` opens the image dialog; rotate buttons require the image plugin (they toggle CSS transform)

    // --- Context menu on right-click ---
    contextmenu: 'link image media table',

    /* --- Paste/drag-drop images --- */
    automatic_uploads: true,          // drag/drop & paste will try to upload
    images_file_types: 'jpeg,jpg,jpe,jfi,jif,jfif,png,webp,gif,bmp,mp4',
    images_reuse_filename: true,

    // --- Upload from PC (YOUR endpoint) ---
    // Called for paste/drag/drop & the image dialog when a file is selected
    images_upload_handler: async (blobInfo, progress) => {
        // // Replace with your own upload endpoint
        // const endpoint = '/api/upload-image'
        // const form = new FormData()
        // form.append('file', blobInfo.blob(), blobInfo.filename())

        // const res = await fetch(endpoint, {
        //     method: 'POST',
        //     body: form,
        //     // include auth headers if required
        // })
        // if (!res.ok) throw new Error('Upload failed')
        // const data = await res.json()
        // // Must return the absolute/serving URL of the uploaded image
        // return data.location || data.url

        // return URL.createObjectURL(blobInfo.blob());
    },

    /* --- Image dialog: add PC upload + your own "Browse" button --- */
    // This lets you add your own file browser (server-side library)
    // file_picker_types: 'file image media',
    // file_picker_callback: (callback, value, meta) => {
    //     if (meta.filetype === 'image' || meta.filetype === 'media') {
    //         tinymce.activeEditor.windowManager.openUrl({
    //             title: 'Şəkil qalereyası',
    //             url: '/file-browser?type=image',
    //             width: 900,
    //             height: 600,
    //             buttons: [{ type: 'cancel', text: 'Close' }],
    //             onMessage: (api, details) => {
    //                 // Accept both formats:
    //                 // A) { mceAction: 'select', url, title }
    //                 // B) { type: 'select', url, title }
    //                 const d = details || {};
    //                 const action = d.mceAction || d.type;
    //                 if (action === 'select' && d.url) {
    //                     callback(d.url, { title: d.title || '' });
    //                     api.close(); // ✅ close TinyMCE dialog here
    //                 }
    //             }
    //         });
    //     }
    // },
    file_picker_types: 'file image media',
    file_picker_callback: (callback, value, meta) => {
        const t = meta.filetype === 'file' ? 'file' : meta.filetype
        tinymce.activeEditor.windowManager.openUrl({
            title: t === 'image' ? 'Şəkil qalereyası' : (t === 'media' ? 'Video qalereyası' : 'Fayl qalereyası'),
            url: `/file_browser?type=${encodeURIComponent(t)}`,
            width: 1000,
            height: 650,
            buttons: [{ type: 'cancel', text: 'Close' }],
            onMessage: (api, details) => {
                const d = details || {}
                const action = d.mceAction || d.type
                if (action === 'select' && d.url) {
                    callback(d.url, { title: d.title || '' })
                    api.close()
                }
            }
        })
    },

    // file_picker_callback1: (callback, value, meta) => {
    //     if (meta.filetype === 'image' || meta.filetype === 'media') {
    //         // 1) Let user pick local files (fallback)
    //         const input = document.createElement('input')
    //         input.type = 'file'
    //         input.accept = 'image/*'
    //         input.onchange = async () => {
    //             const file = input.files?.[0]
    //             if (!file) return
    //             // optional: push through your upload endpoint to get a final URL
    //             // const form = new FormData()
    //             // form.append('file', file, file.name)
    //             // const res = await fetch('/api/upload-image', { method: 'POST', body: form })
    //             // const data = await res.json()
    //             // callback(data.location || data.url, { title: file.name })
    //             callback(URL.createObjectURL(file), { title: file.name })
    //         }
    //         input.click()

    //         // 2) (Optional) Also open your server file browser in a Tiny dialog
    //         // Comment out if you don't need both
    //         tinymce.activeEditor.windowManager.openUrl({
    //             title: 'Image Library',
    //             url: '/file_browser?type=image',  // your own image browser page
    //             width: 900,
    //             height: 600,
    //             buttons: [
    //                 { type: 'cancel', text: 'Close' }
    //             ],
    //             onMessage: (api, details) => {
    //                 // Your /file-browser should postMessage({ type:'select', url:'...' })
    //                 if (details?.type === 'select' && details?.url) {
    //                     callback(details.url, { title: details.title || '' })
    //                     api.close()
    //                 }
    //             }
    //         })
    //     }

    //     if (meta.filetype === 'media') {
    //         // Similar flow for videos/audio if you need
    //         tinymce.activeEditor.windowManager.openUrl({
    //             title: 'Media Library',
    //             url: '/file_browser?type=media',
    //             width: 900,
    //             height: 600,
    //             buttons: [{ type: 'cancel', text: 'Close' }],
    //             onMessage: (api, details) => {
    //                 if (details?.type === 'select' && details?.url) {
    //                     callback(details.url, { title: details.title || '' })
    //                     api.close()
    //                 }
    //             }
    //         })
    //     }
    // },

    // --- Custom toolbar button to open the Image dialog for the current selection ---
    setup: (editor) => {
        // editor.ui.registry.addButton('imageprops', {
        //     icon: 'image',
        //     tooltip: 'Image properties',
        //     onAction: () => {
        //         // If an image is selected, open the image dialog.
        //         // TinyMCE 6: `image` button opens the dialog; this ensures it’s always visible.
        //         editor.execCommand('mceImage'); // opens insert/edit dialog depending on selection
        //     }
        // });
        // Simple CSS-based rotate buttons (90° steps)
        // const rotate = (delta) => {
        //     const img = editor.selection.getNode();
        //     if (img && img.nodeName === 'IMG') {
        //         const cur = parseInt(img.getAttribute('data-rot') || '0', 10);
        //         const next = (cur + delta + 360) % 360;
        //         img.style.transform = `rotate(${next}deg)`;     // visual rotate
        //         img.style.transformOrigin = 'center center';
        //         img.setAttribute('data-rot', String(next));     // remember rotation
        //     }
        // };
        // editor.ui.registry.addButton('rotate90L', {
        //     icon: 'rotate-left',
        //     tooltip: 'Rotate left 90°',
        //     onAction: () => rotate(-90),
        // });
        // editor.ui.registry.addButton('rotate90R', {
        //     icon: 'rotate-right',
        //     tooltip: 'Rotate right 90°',
        //     onAction: () => rotate(90),
        // });
    },
}
</script>


<style>
.tox .tox-tab {
    cursor: pointer;
    padding: 4px 6px;
    /* border: 0 0 1px 0 solid black; */
    width: 100%;
    margin: 0px;
}

.tox .tox-dialog__header {
    /* border-width: 3px; */
    /* border-style: solid;
    border-color: #cdcdcd; */
    border-bottom: 1px solid #cdcdcd !important;
}

.tox .tox-dialog__footer {
    border-top: 1px solid #cdcdcd !important;
}
</style>